import { useMemo } from 'react';

// Arbitrary type for sortable market data
// Can sort any market data that has the matching keys
export type SortableMarketData = {
	volume: number;
	pricePercentChange: number;
	symbol: string;
	volume30d?: number;
	funding?: number;
	openInterest?: number;
};

export type UISortDirection = 'high' | 'low';

export type SpotMarketSortType =
	| 'default'
	| 'symbol'
	| 'volume'
	| 'volume30d'
	| 'pricePercentChange';

export type PerpMarketSortType =
	| 'default'
	| 'symbol'
	| 'volume'
	| 'volume30d'
	| 'pricePercentChange'
	| 'funding'
	| 'openInterest';

export const spotMarketSortTypes: SpotMarketSortType[] = [
	'default',
	'symbol',
	'volume',
	'volume30d',
	'pricePercentChange',
	'pricePercentChange',
];

export const perpMarketSortTypes: PerpMarketSortType[] = [
	'default',
	'symbol',
	'volume',
	'volume30d',
	'pricePercentChange',
	'pricePercentChange',
	'funding',
	'openInterest',
];

export const sortOptionToId = (
	sortType: SpotMarketSortType | PerpMarketSortType,
	sortDirection: UISortDirection
) => {
	return `${sortType}_${sortDirection}`;
};

export const sortOptionFromId = (id: string) => {
	const [sortType, sortDirection] = id.split('_') as [
		SpotMarketSortType | PerpMarketSortType,
		UISortDirection | undefined
	];
	return { sortType, sortDirection };
};

export const useSortMarkets = (
	markets: SortableMarketData[],
	sortOption: {
		sortType: PerpMarketSortType | SpotMarketSortType;
		sortDirection: UISortDirection | undefined;
	},
	isSpot?: boolean
) => {
	return useMemo(() => {
		if (!markets) return [];

		if (sortOption.sortType === 'default') return markets;

		if (sortOption.sortType === 'symbol') {
			return markets.slice().sort((a, b) => {
				const symbolA = a.symbol;
				const symbolB = b.symbol;

				return sortOption.sortDirection === 'high'
					? symbolA.localeCompare(symbolB)
					: symbolB.localeCompare(symbolA);
			});
		}

		const sorted = markets.slice().sort((a, b) => {
			const sortValueA = a[sortOption.sortType];
			const sortValueB = b[sortOption.sortType];

			return sortOption.sortDirection === 'high'
				? sortValueB - sortValueA
				: sortValueA - sortValueB;
		});

		return sorted;
	}, [markets, sortOption.sortType, sortOption.sortDirection, isSpot]);
};
